// apps/office/src/components/office/ReceiptPDF.jsx
import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import receiptBg from '../../pages/public/Receipt.png';

const C = {
  primary:   '#059669',
  primaryLt: '#ecfdf5',
  accent:    '#0d9488',
  accentLt:  '#f0fdfa',
  success:   '#16a34a',
  danger:    '#dc2626',
  dangerLt:  '#fef2f2',
  ink:       '#0f172a',
  body:      '#334155',
  muted:     '#64748b',
  border:    '#e2e8f0',
};

const S = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: C.body,
  },
  bg: { position: 'absolute', top: 0, left: 0, width: 595.28, height: 841.89 },

  content: {
    paddingHorizontal: 40,
    paddingTop: 195,
    paddingBottom: 190,
  },

  /* ── Top card ── */
  topCard: {
    flexDirection: 'row',
    backgroundColor: C.primaryLt,
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 10,
    borderLeftWidth: 3,
    borderLeftColor: C.primary,
  },
  clientCol: { flex: 1, padding: 10, paddingRight: 6 },
  clientLabel: {
    fontSize: 7, color: C.primary, fontWeight: 'bold',
    letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4,
  },
  clientName: { fontSize: 12, fontWeight: 'bold', color: C.ink, marginBottom: 2 },
  clientLine: { fontSize: 8.5, color: C.body, marginBottom: 1.5, lineHeight: 1.3 },
  jobIdTag: {
    marginTop: 4,
    alignSelf: 'flex-start',
    backgroundColor: C.primary,
    color: '#fff',
    paddingVertical: 1.5, paddingHorizontal: 6,
    borderRadius: 3,
    fontSize: 7.5, fontWeight: 'bold',
  },
  metaCol: {
    width: 155,
    backgroundColor: '#ffffff',
    padding: 10,
    borderLeftWidth: 1,
    borderLeftColor: C.border,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 2.5,
    borderBottomWidth: 0.4,
    borderBottomColor: C.border,
  },
  metaRowLast: { borderBottomWidth: 0 },
  metaLbl: { fontSize: 7, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 },
  metaVal: { fontSize: 8.5, color: C.ink, fontWeight: 'bold' },
  metaNum: { fontSize: 11, fontWeight: 'bold', color: C.primary, letterSpacing: 0.3 },

  /* ── Amount block ── */
  amountBlock: {
    backgroundColor: C.primary,
    borderRadius: 6,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 10,
    alignItems: 'center',
  },
  amountLbl: {
    fontSize: 8, color: '#bbf7d0',
    letterSpacing: 2, textTransform: 'uppercase', marginBottom: 3,
  },
  amountVal: { fontSize: 22, color: '#ffffff', fontWeight: 'bold' },
  amountSub: { fontSize: 7.5, color: '#d1fae5', marginTop: 2 },

  /* ── Quotation ref bar ── */
  refBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: C.accentLt,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 4,
    marginTop: 4,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: C.accent,
  },
  refLbl: {
    fontSize: 7.5, color: C.accent, fontWeight: 'bold',
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  refVal: { fontSize: 10, color: C.ink, fontWeight: 'bold' },

  /* ── NEW: Paid & Due compact block ── */
  balanceBlock: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    borderRadius: 4,
    paddingVertical: 7,
    paddingHorizontal: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: C.border,
  },
  balanceCol: {
    flex: 1,
    alignItems: 'center',
  },
  balanceDivider: {
    width: 1,
    backgroundColor: C.border,
  },
  balanceLbl: {
    fontSize: 7.5, color: C.muted,
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3,
  },
  balancePaid: { fontSize: 12, color: C.success, fontWeight: 'bold' },
  balanceDue:  { fontSize: 12, color: C.danger,  fontWeight: 'bold' },
  balanceOk:   { fontSize: 12, color: C.success, fontWeight: 'bold' },

  /* ── Details table ── */
  detailsTable: { marginBottom: 8 },
  detailsHead: {
    flexDirection: 'row',
    backgroundColor: '#1e293b',
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
  },
  detailsHeadCell: { color: '#ffffff', fontSize: 8, fontWeight: 'bold', letterSpacing: 0.3 },
  detailsRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 0.4,
    borderBottomColor: C.border,
  },
  detailsRowAlt: { backgroundColor: '#f8fafc' },
  detailsCell: { fontSize: 9, color: C.body },
  detailsCellBold: { fontSize: 9, color: C.ink, fontWeight: 'bold' },
  cLabel: { width: 110 },
  cValue: { flex: 1 },

  /* ── Notes ── */
  notes: {
    padding: 8,
    backgroundColor: '#fffbeb',
    borderRadius: 3,
    borderLeftWidth: 3,
    borderLeftColor: '#f59e0b',
  },
  notesLbl: {
    fontSize: 7.5, color: '#92400e', fontWeight: 'bold',
    textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3,
  },
  notesTxt: { fontSize: 8, color: '#44403c', lineHeight: 1.4 },
});

const fmtCurrency = (v) => {
  const n = Number(v) || 0;
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
  return `Rs. ${formatted}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
};

const buildReceiptNo = (payment) => {
  if (payment?.receiptNumber) return payment.receiptNumber;
  const id = String(payment?.id || '').replace(/-/g, '');
  const tail = id.slice(-5).toUpperCase() || 'XXXXX';
  return `RCP-${tail}`;
};

const ReceiptPDF = ({
  payment,
  client,
  quotation,
  totalPaidForQuote,   // ← NEW: total paid against this quotation
}) => {
  const receiptNumber = buildReceiptNo(payment);

  /* Compute paid + due */
  const quoteTotal = Number(quotation?.total) || 0;
  const currentPayment = Number(payment?.amount) || 0;

  // If parent passes totalPaidForQuote use it (already includes this payment);
  // else fallback to this payment only
  const totalPaid = Number(totalPaidForQuote ?? currentPayment) || 0;
  const totalDue = Math.max(0, quoteTotal - totalPaid);
  const isFullyPaid = totalDue <= 0 && quoteTotal > 0;

  return (
    <Document
      title={`Receipt ${receiptNumber}`}
      author="HouseDesigns99"
      subject="Payment Receipt"
    >
      <Page size="A4" style={S.page}>
        <Image src={receiptBg} style={S.bg} fixed />

        <View style={S.content}>
          {/* ─── Top card ─── */}
          <View style={S.topCard}>
            <View style={S.clientCol}>
              <Text style={S.clientLabel}>Received From</Text>
              <Text style={S.clientName}>{client?.name || payment?.clientName || '—'}</Text>
              {!!client?.phone && <Text style={S.clientLine}>Phone: {client.phone}</Text>}
              {!!client?.email && <Text style={S.clientLine}>Email: {client.email}</Text>}
              {!!client?.address && <Text style={S.clientLine}>Address: {client.address}</Text>}
              {!!client?.jobId && (
                <Text style={S.jobIdTag}>Job ID: {client.jobId}</Text>
              )}
            </View>

            <View style={S.metaCol}>
              <View style={S.metaRow}>
                <Text style={S.metaLbl}>Receipt No.</Text>
                <Text style={S.metaNum}>{receiptNumber}</Text>
              </View>
              <View style={S.metaRow}>
                <Text style={S.metaLbl}>Date</Text>
                <Text style={S.metaVal}>{fmtDate(payment?.paymentDate || new Date())}</Text>
              </View>
              <View style={[S.metaRow, S.metaRowLast]}>
                <Text style={S.metaLbl}>Status</Text>
                <Text style={S.metaVal}>{payment?.status || 'Completed'}</Text>
              </View>
            </View>
          </View>

          {/* ─── Amount block ─── */}
          <View style={S.amountBlock}>
            <Text style={S.amountLbl}>Amount Received</Text>
            <Text style={S.amountVal}>{fmtCurrency(payment?.amount)}</Text>
            <Text style={S.amountSub}>Payment received with thanks</Text>
          </View>

          {/* ─── Quotation ref ─── */}
          {!!quotation?.quoteNumber && (
            <View style={S.refBar}>
              <Text style={S.refLbl}>Against Quotation</Text>
              <Text style={S.refVal}>
                {quotation.quoteNumber}  ·  {fmtCurrency(quotation.total)}
              </Text>
            </View>
          )}

          {/* ─── NEW: Total Paid & Due summary ─── */}
          {quoteTotal > 0 && (
            <View style={S.balanceBlock}>
              <View style={S.balanceCol}>
                <Text style={S.balanceLbl}>Total Paid</Text>
                <Text style={S.balancePaid}>{fmtCurrency(totalPaid)}</Text>
              </View>
              <View style={S.balanceDivider} />
              <View style={S.balanceCol}>
                <Text style={S.balanceLbl}>Balance Due</Text>
                <Text style={isFullyPaid ? S.balanceOk : S.balanceDue}>
                  {isFullyPaid ? 'PAID IN FULL' : fmtCurrency(totalDue)}
                </Text>
              </View>
            </View>
          )}

          {/* ─── Details table ─── */}
          <View style={S.detailsTable}>
            <View style={S.detailsHead} fixed>
              <Text style={[S.detailsHeadCell, S.cLabel]}>Field</Text>
              <Text style={[S.detailsHeadCell, S.cValue]}>Details</Text>
            </View>

            <View style={[S.detailsRow, S.detailsRowAlt]}>
              <Text style={[S.detailsCell, S.cLabel]}>Payment Method</Text>
              <Text style={[S.detailsCellBold, S.cValue]}>{payment?.paymentMethod || '—'}</Text>
            </View>

            <View style={S.detailsRow}>
              <Text style={[S.detailsCell, S.cLabel]}>Transaction Ref.</Text>
              <Text style={[S.detailsCellBold, S.cValue]}>
                {payment?.transactionId || payment?.reference || '—'}
              </Text>
            </View>
          </View>

          {/* ─── Notes ─── */}
          <View style={S.notes}>
            <Text style={S.notesLbl}>Note</Text>
            <Text style={S.notesTxt}>
              This is a computer-generated receipt. Please keep it for your records.
              Contact us within 7 days for any discrepancies.
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ReceiptPDF;
